import { useAccount } from 'wagmi'
import { useUserNFTs, type NFTData } from '@/hooks/useUserNFTs'
import { useRefund } from '@/hooks/useRefund'
import { Card } from './ui/Card'
import { Button } from './ui/Button'
import { Loader2, Image as ImageIcon, Clock, DollarSign } from 'lucide-react'
import { useState, useEffect } from 'react'
import { fetchIpfsJson, buildIpfsGatewayUrls } from '@/lib/utils'

interface NFTCardProps {
  nft: NFTData
  isProcessing: boolean
  onRefund: (tokenId: number) => void
}

const NFTCard = ({ nft, isProcessing, onRefund }: NFTCardProps) => {
  const [imageUrl, setImageUrl] = useState<string>('')
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true)
  const [imageError, setImageError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    let cancelled = false

    const loadMetadata = async () => {
      try {
        setIsImageLoading(true)
        setImageError(null)

        if (!nft.tokenURI || nft.tokenURI.trim() === '') {
          if (!cancelled) {
            setImageError('Base URI не установлен? Вызови reveal(baseURI) в Admin.')
            setImageUrl('')
            setIsImageLoading(false)
          }
          return
        }

        const { data } = await fetchIpfsJson<{
          image?: string
          image_url?: string
          image_small?: string
          thumbnail?: string
        }>(nft.tokenURI)

        // Prefer lightweight preview fields if present
        const imgRef =
          data.image_small ??
          data.thumbnail ??
          data.image ??
          data.image_url
        if (!imgRef) {
          throw new Error('В JSON нет поля "image" или "image_url"')
        }

        const urls = buildIpfsGatewayUrls(imgRef)
        if (!urls.length) {
          throw new Error('Не удалось построить HTTP URL для картинки из IPFS URI')
        }

        if (!cancelled) {
          setImageUrls(urls)
          setImageUrl(urls[0])
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        console.warn(`[NFT #${nft.tokenId}] tokenURI=${nft.tokenURI}`, msg)
        if (!cancelled) {
          const short = nft.tokenURI.length > 40 ? `${nft.tokenURI.slice(0, 36)}...` : nft.tokenURI
          setImageError(short ? `Ошибка: ${msg}. URI: ${short}` : msg)
        }
      } finally {
        if (!cancelled) {
          setIsImageLoading(false)
        }
      }
    }

    if (nft.tokenURI) {
      loadMetadata()
    } else {
      setImageError('Нет tokenURI (коллекция не revealed?)')
      setImageUrls([])
      setImageUrl('')
      setIsImageLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [nft.tokenId, nft.tokenURI, retryCount])

  const handleRefundClick = () => {
    onRefund(Number(nft.tokenId))
  }

  return (
    <div
      className="bg-slate-800/50 rounded-lg overflow-hidden border border-slate-700 hover:border-purple-500/50 transition-all"
    >
      {/* NFT Image */}
      <div className="aspect-square bg-gradient-to-br from-purple-900/50 to-pink-900/50 flex items-center justify-center relative group overflow-hidden">
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={`NFT #${nft.tokenId.toString()}`}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={() => {
              setImageUrl((currentUrl) => {
                const currentIndex = imageUrls.indexOf(currentUrl)
                const nextIndex = currentIndex >= 0 ? currentIndex + 1 : 1
                const hasNext = nextIndex < imageUrls.length

                console.warn(
                  `[NFT #${nft.tokenId}] image load failed for ${currentUrl}; ` +
                    (hasNext
                      ? `trying fallback gateway: ${imageUrls[nextIndex]}`
                      : 'no more gateway fallbacks available'),
                )

                if (hasNext) {
                  return imageUrls[nextIndex]
                }

                setImageError('Картинка не загрузилась (IPFS/gateway?)')
                return ''
              })
            }}
          />
        ) : (
          <ImageIcon className="w-16 h-16 text-slate-600" />
        )}

        {isImageLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}

        {imageError && !isImageLoading && (
          <div className="absolute bottom-2 left-2 right-2 flex flex-col gap-1">
            <div className="bg-red-500/80 text-xs text-white px-2 py-1 rounded">
              {imageError}
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="text-xs"
              onClick={() => {
                setImageError(null)
                setRetryCount((c) => c + 1)
              }}
            >
              Повторить загрузку
            </Button>
          </div>
        )}

        {nft.eligible && (
          <div className="absolute top-2 right-2">
            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              Refundable
            </span>
          </div>
        )}
      </div>

      {/* NFT Details */}
      <div className="p-4">
        <h4 className="text-lg font-bold text-white mb-3">
          NFT #{nft.tokenId.toString()}
        </h4>

        {/* Refund Info */}
        {nft.eligible ? (
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Time Left:
              </span>
              <span className="text-yellow-400 font-medium">
                {nft.timeLeftFormatted}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400 flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                Refund:
              </span>
              <span className="text-green-400 font-medium">
                {nft.refundAmountFormatted} ETH/BNB
              </span>
            </div>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-400 text-center">
              Refund period expired
            </p>
          </div>
        )}

        {/* Refund Button */}
        {nft.eligible && (
          <Button
            onClick={handleRefundClick}
            disabled={isProcessing}
            variant="destructive"
            className="w-full"
            size="sm"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              'Request Refund'
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

export const MyNFTs = () => {
  const { address, isConnected } = useAccount()
  const { nfts, isLoading, hasNFTs } = useUserNFTs(address)
  const { refund, isLoading: isRefunding, isConfirming } = useRefund()
  const [selectedTokenId, setSelectedTokenId] = useState<number | null>(null)

  if (!isConnected) {
    return (
      <Card>
        <div className="text-center py-12">
          <ImageIcon className="w-16 h-16 mx-auto text-slate-600 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">My NFTs</h3>
          <p className="text-slate-400">Connect your wallet to view your NFTs</p>
        </div>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card>
        <h3 className="text-2xl font-bold text-white mb-6">My NFTs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-square bg-slate-700 rounded-lg mb-4"></div>
              <div className="h-4 bg-slate-700 rounded mb-2"></div>
              <div className="h-4 bg-slate-700 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  if (!hasNFTs) {
    return (
      <Card>
        <div className="text-center py-12">
          <ImageIcon className="w-16 h-16 mx-auto text-slate-600 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">My NFTs</h3>
          <p className="text-slate-400 mb-4">You don't own any NFTs yet</p>
          <p className="text-sm text-slate-500">Mint your first NFT above!</p>
        </div>
      </Card>
    )
  }

  const handleRefund = (tokenId: number) => {
    setSelectedTokenId(tokenId)
    refund(tokenId)
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-white">My NFTs</h3>
        <span className="text-slate-400">
          {nfts.length} NFT{nfts.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {nfts.map((nft) => {
          const isProcessing = selectedTokenId === Number(nft.tokenId) && (isRefunding || isConfirming)

          return (
            <NFTCard
              key={nft.tokenId.toString()}
              nft={nft}
              isProcessing={isProcessing}
              onRefund={handleRefund}
            />
          )
        })}
      </div>

      {/* Refund Info Banner */}
      <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
        <p className="text-purple-300 text-sm text-center">
          💡 You can refund any NFT within 7 days of minting for 90% of the mint price
        </p>
      </div>
    </Card>
  )
}
